/**
 * MONSTER TOWN — ECONOMY. FROZEN CONTRACT (src/core/*). Additive only.
 *
 * DESIGN RULES (from the brief, not negotiable):
 *  - Coins come EASILY. This is never a grind. If a kid wants a thing, they get
 *    it in a few minutes of play, not a session of chores.
 *  - BASICS ARE FREE FOREVER. Blocks, floors, walls, paths and plain furniture
 *    cost 0. A kid can NEVER be stuck unable to build. You only buy fun stuff.
 *  - Nothing is ever taken away. No fees, no upkeep, no debt. `spend` cannot
 *    take you below zero, it simply declines with a friendly shrug (LAW #2).
 *  - The unlock ladder is driven by LIFETIME EARNED, never by current balance,
 *    so buying something never un-unlocks anything.
 *
 * Prices live on the `PlaceableDef.cost`. This file owns the sources, the
 * ladder, and the wallet. Price BALANCE data (multipliers, starter grants,
 * tier tables) lives in `src/content/economy.ts` — a data drop, not code.
 */

import { bus } from './events'
import { allPlaceables, type PlaceableCategory, requirePlaceable } from './placeable'
import type { PlaceableId, WalletState } from './types'

// ---------------------------------------------------------------------------
// free forever
// ---------------------------------------------------------------------------

/** Categories that are ALWAYS cost 0 regardless of what a def claims. */
export const FREE_FOREVER_CATEGORIES: ReadonlySet<PlaceableCategory> = new Set<PlaceableCategory>([
  'block',
  'floor',
  'wall',
  'path',
  'door',
  'window',
  'stair',
])

/** Individual free-forever ids beyond the free categories (plain furniture). */
export const FREE_FOREVER_IDS = new Set<PlaceableId>()

export function markFreeForever(...ids: PlaceableId[]): void {
  for (const id of ids) FREE_FOREVER_IDS.add(id)
}

export function isFreeForever(id: PlaceableId): boolean {
  if (FREE_FOREVER_IDS.has(id)) return true
  const def = requirePlaceable(id)
  return FREE_FOREVER_CATEGORIES.has(def.category) || def.cost <= 0
}

/** The authoritative price. Free-forever always wins over a def's cost. */
export function priceOf(id: PlaceableId): number {
  if (isFreeForever(id)) return 0
  return Math.max(0, Math.round(requirePlaceable(id).cost))
}

// ---------------------------------------------------------------------------
// coin sources
// ---------------------------------------------------------------------------

/**
 * Every way a coin can enter the world. Add entries freely (additive); the
 * amounts live in content so balance is a data change.
 */
export type CoinSource =
  | 'venue.served'
  | 'venue.tip'
  | 'venue.perfectOrder'
  | 'town.job'
  | 'town.visitor'
  | 'town.find' // a coin lying in the grass
  | 'daily.return'
  | 'daily.gift'
  | 'garden.harvest'
  | 'starter'
  | 'debug'

export interface CoinRule {
  readonly source: CoinSource
  readonly base: number
  /** Random bonus range added on top, 0..bonus inclusive. Keeps it delightful. */
  readonly bonus?: number
}

const RULES = new Map<CoinSource, CoinRule>()

export function setCoinRules(rules: readonly CoinRule[]): void {
  for (const r of rules) RULES.set(r.source, r)
}

export function coinRule(source: CoinSource): CoinRule {
  return RULES.get(source) ?? { source, base: 1 }
}

// ---------------------------------------------------------------------------
// unlock ladder
// ---------------------------------------------------------------------------

export interface UnlockTier {
  readonly id: string
  /** Lifetime coins earned needed to reach this tier. */
  readonly lifetimeEarned: number
  /** Placeables this tier puts in the shop. */
  readonly grants: readonly PlaceableId[]
  /** Optional scene unlocked (a new room, the venue plot). */
  readonly grantsScene?: string
  /** Picture shown in the unlock celebration. No words. LAW #4. */
  readonly icon: string
}

let LADDER: readonly UnlockTier[] = []

export function setUnlockLadder(tiers: readonly UnlockTier[]): void {
  LADDER = [...tiers].sort((a, b) => a.lifetimeEarned - b.lifetimeEarned)
}

export function unlockLadder(): readonly UnlockTier[] {
  return LADDER
}

// ---------------------------------------------------------------------------
// wallet
// ---------------------------------------------------------------------------

export type SpendResult =
  | { ok: true; spent: number; balance: number }
  | { ok: false; reason: 'notEnough' | 'locked'; need: number; have: number }

export class Wallet {
  private state: WalletState = { coins: 0, lifetimeEarned: 0, tiers: [], owned: [] }
  /** Seeded rng so bonus coins are deterministic under the harness. */
  rng: () => number = Math.random

  snapshot(): WalletState {
    return {
      coins: this.state.coins,
      lifetimeEarned: this.state.lifetimeEarned,
      tiers: [...this.state.tiers],
      owned: [...this.state.owned],
    }
  }

  restore(s: WalletState): void {
    this.state = {
      coins: Math.max(0, Math.floor(s.coins)),
      lifetimeEarned: Math.max(0, Math.floor(s.lifetimeEarned)),
      tiers: [...s.tiers],
      owned: [...s.owned],
    }
    this.checkLadder()
  }

  get coins(): number {
    return this.state.coins
  }

  get lifetimeEarned(): number {
    return this.state.lifetimeEarned
  }

  /** Award coins. Always succeeds. Emits `economy:earned` for the coin VFX. */
  earn(source: CoinSource, at?: { x: number; y: number; z: number }, multiplier = 1): number {
    const rule = coinRule(source)
    const bonus = rule.bonus === undefined ? 0 : Math.floor(this.rng() * (rule.bonus + 1))
    const amount = Math.max(0, Math.round((rule.base + bonus) * multiplier))
    if (amount === 0) return 0
    this.state.coins += amount
    this.state.lifetimeEarned += amount
    bus.emit('economy:earned', at === undefined ? { amount, source } : { amount, source, at })
    this.checkLadder()
    return amount
  }

  /** Award an exact amount (venue tips that scale with the order). */
  earnExact(amount: number, source: string, at?: { x: number; y: number; z: number }): number {
    const a = Math.max(0, Math.round(amount))
    if (a === 0) return 0
    this.state.coins += a
    this.state.lifetimeEarned += a
    bus.emit('economy:earned', at === undefined ? { amount: a, source } : { amount: a, source, at })
    this.checkLadder()
    return a
  }

  canAfford(id: PlaceableId): boolean {
    return this.state.coins >= priceOf(id)
  }

  owns(id: PlaceableId): boolean {
    return isFreeForever(id) || this.state.owned.includes(id)
  }

  /** Is this in the shop yet? (tierless defs are available on day one) */
  isUnlocked(id: PlaceableId): boolean {
    const tier = requirePlaceable(id).tier
    return tier === undefined || this.state.tiers.includes(tier)
  }

  /** Buying is the ONLY way coins leave the wallet. Never goes negative. */
  buy(id: PlaceableId): SpendResult {
    const cost = priceOf(id)
    if (!this.isUnlocked(id)) {
      bus.emit('economy:rejected', { need: cost, have: this.state.coins, on: id })
      return { ok: false, reason: 'locked', need: cost, have: this.state.coins }
    }
    if (cost > this.state.coins) {
      bus.emit('economy:rejected', { need: cost, have: this.state.coins, on: id })
      return { ok: false, reason: 'notEnough', need: cost, have: this.state.coins }
    }
    this.state.coins -= cost
    if (!this.state.owned.includes(id) && cost > 0) this.state.owned.push(id)
    bus.emit('economy:spent', { amount: cost, on: id })
    return { ok: true, spent: cost, balance: this.state.coins }
  }

  /**
   * Placing an already-owned or free item costs nothing. Free-forever items are
   * infinitely placeable; bought items are also infinitely placeable once owned
   * (you buy the CATALOGUE ENTRY, not the object). This is deliberate: a kid
   * who saved for a nice lamp gets to put lamps everywhere.
   */
  canPlace(id: PlaceableId): boolean {
    return this.owns(id) && this.isUnlocked(id)
  }

  private checkLadder(): void {
    for (const tier of LADDER) {
      if (this.state.tiers.includes(tier.id)) continue
      if (this.state.lifetimeEarned >= tier.lifetimeEarned) {
        this.state.tiers.push(tier.id)
        bus.emit('economy:unlocked', { tier: tier.id, grants: [...tier.grants] })
      }
    }
  }

  /** Everything currently purchasable, for the shop UI. */
  shopCatalogue(): { id: PlaceableId; cost: number; owned: boolean; affordable: boolean }[] {
    return allPlaceables()
      .filter((d) => !isFreeForever(d.id) && this.isUnlocked(d.id))
      .map((d) => ({
        id: d.id,
        cost: priceOf(d.id),
        owned: this.owns(d.id),
        affordable: this.canAfford(d.id),
      }))
  }

  /** Everything placeable right now, for the build palette. */
  buildPalette(): PlaceableId[] {
    return allPlaceables()
      .filter((d) => this.canPlace(d.id))
      .map((d) => d.id)
  }
}

export const wallet = new Wallet()
